import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createProductFuse, searchProducts } from './utils/search';
import { useRazorpay } from './utils/razorpay';
import Login from './components/Login';
import Register from './components/Register';
import AdminDashboard from './components/AdminDashboard';
import ProductGrid from './components/ProductGrid';
import Header from './components/Header';
import CartSummary from './components/CartSummary';
import SuccessModal from './components/SuccessModal';
import CustomizationModal from './components/CustomizationModal';
import CategoryFilter from './components/CategoryFilter';
import CartPage from './components/CartPage';
import OrderHistory from './components/OrderHistory';

const API_BASE = process.env.REACT_APP_API_URL || '';

function normalizePath(pathname) {
  if (!pathname || pathname === '/') return '/';
  return pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
}

function getStoredSession() {
  try {
    const token = localStorage.getItem('authToken');
    const storedUser = localStorage.getItem('authUser');
    const user = storedUser ? JSON.parse(storedUser) : null;
    return { token, user };
  } catch (error) {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    return { token: null, user: null };
  }
}

function getPageFromPath(pathname, isAuthenticated, isAdmin) {
  const path = normalizePath(pathname);

  if (!isAuthenticated) {
    return path === '/register' ? 'register' : 'login';
  }

  switch (path) {
    case '/':
    case '/products':
    case '/login':
    case '/register':
      return 'products';
    case '/cart':
      return 'cart';
    case '/my-orders':
      return 'orders';
    case '/admin':
      return isAdmin ? 'admin' : 'products';
    default:
      return 'products';
  }
}

function getPathForPage(page, isAuthenticated, isAdmin) {
  if (!isAuthenticated) {
    return page === 'register' ? '/register' : '/login';
  }

  switch (page) {
    case 'cart':
      return '/cart';
    case 'orders':
      return '/my-orders';
    case 'admin':
      return isAdmin ? '/admin' : '/';
    case 'products':
    default:
      return '/';
  }
}

function App() {
  const [user, setUser] = useState(() => getStoredSession().user);
  const [token, setToken] = useState(() => getStoredSession().token);
  const [currentPage, setCurrentPage] = useState(() => {
    const session = getStoredSession();
    return getPageFromPath(
      window.location.pathname,
      Boolean(session.token && session.user),
      session.user?.role === 'admin'
    );
  }); // login, register, products, orders, cart, admin
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submittedCount, setSubmittedCount] = useState(0); // Store count for success modal
  const [googleData, setGoogleData] = useState(null); // For Google SSO registration flow
  const [pendingProduct, setPendingProduct] = useState(null); // Product waiting for design upload
  const [pendingQty, setPendingQty] = useState(1); // Quantity chosen on the card before customization
  const [paymentConfig, setPaymentConfig] = useState({ paymentMethod: 'COD' }); // From /api/config
  const loadRazorpay = useRazorpay();

  // Fetch payment config on mount — public endpoint, no auth required.
  // The server's paymentMethod is authoritative; client state is display-only.
  useEffect(() => {
    fetch(`${API_BASE}/api/config`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setPaymentConfig(data); })
      .catch(() => {}); // silently keep default COD if unreachable
  }, []);

  // Fetch products when user logs in
  useEffect(() => {
    if (user && token && currentPage === 'products') {
      fetchProducts();
    }
  }, [user, token, currentPage]);

  useEffect(() => {
    const handlePopState = () => {
      setError('');
      setCurrentPage(
        getPageFromPath(
          window.location.pathname,
          Boolean(user && token),
          user?.role === 'admin'
        )
      );
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [user, token]);

  useEffect(() => {
    const nextPath = getPathForPage(currentPage, Boolean(user && token), user?.role === 'admin');
    if (window.location.pathname !== nextPath) {
      window.history.pushState({ page: currentPage }, '', nextPath);
    }
  }, [currentPage, user, token]);

  const fetchProducts = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/api/products`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();

      if (response.status === 401 || response.status === 403) {
        handleLogout();
        return;
      }

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch products');
      }

      setProducts(data.products || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    localStorage.setItem('authToken', authToken);
    localStorage.setItem('authUser', JSON.stringify(userData));
    setCurrentPage('products');
  };

  const handleLogout = async () => {
    if (token) {
      try {
        await fetch(`${API_BASE}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      } catch (err) {
        // Ignore logout errors
      }
    }
    setUser(null);
    setToken(null);
    setSelectedProducts({});
    setGoogleData(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    setCurrentPage('login');
  };

  // Handle Google SSO registration (new user needs to complete profile)
  const handleGoogleRegister = (credential, email, name, picture) => {
    setGoogleData({ credential, email, name, picture });
    setCurrentPage('register');
  };

  // "Add to Cart" on a card: add directly unless customization is requested.
  const handleAddToCart = useCallback((product, qty, options = {}) => {
    const normalizedQty = Math.max(1, qty || 1);
    if (options.customization === 'yes') {
      setPendingProduct(product);
      setPendingQty(normalizedQty);
      return;
    }

    setSelectedProducts(prev => ({
      ...prev,
      [product.id]: {
        ...product,
        quantity: normalizedQty,
        customization: 'no',
        customizationFile: null
      }
    }));
  }, []);

  // Remove an item that's already in the cart.
  const handleRemoveFromCart = useCallback((productId) => {
    setSelectedProducts(prev => {
      const updated = { ...prev };
      delete updated[productId];
      return updated;
    });
  }, []);

  // Called when user uploads a customization file in the modal.
  const handleCustomizationConfirm = useCallback((file) => {
    if (!pendingProduct) return;
    setSelectedProducts(prev => ({
      ...prev,
      [pendingProduct.id]: {
        ...pendingProduct,
        quantity: pendingQty,
        customization: 'yes',
        customizationFile: file || null
      }
    }));
    setPendingProduct(null);
    setPendingQty(1);
  }, [pendingProduct, pendingQty]);

  // Called when user cancels modal — product not added
  const handleCustomizationCancel = useCallback(() => {
    setPendingProduct(null);
    setPendingQty(1);
  }, []);

  const handleQuantityChange = useCallback((productId, quantity) => {
    setSelectedProducts(prev => {
      if (!prev[productId]) return prev;
      return {
        ...prev,
        [productId]: { ...prev[productId], quantity: Math.max(1, quantity) }
      };
    });
  }, []);

  const handleClearSelection = () => {
    setSelectedProducts({});
    setPendingProduct(null);
    setPendingQty(1);
  };

  // Place the order from the cart page, with the chosen pickup city + store.
  const handlePlaceOrder = async (pickupCity, pickupStore) => {
    const selections = Object.values(selectedProducts);
    if (selections.length === 0) return;

    // Block submit if any "yes" customization is missing a file
    const missingFiles = selections.filter(s => s.customization === 'yes' && !s.customizationFile);
    if (missingFiles.length > 0) {
      setError(`Design file required for: ${missingFiles.map(s => s.description || s.sku).join(', ')}`);
      return;
    }

    setSubmitting(true);
    setError('');

    // RAZORPAY mode: call /api/checkout to get an order, then open the modal.
    // COD falls through to the existing /api/submit path below.
    if (paymentConfig.paymentMethod === 'RAZORPAY') {
      try {
        // --- Build multipart form (same shape as /api/submit) ---
        const formData = new FormData();
        const selectionsData = selections.map((s, index) => {
          if (s.customization === 'yes' && s.customizationFile) {
            formData.append(`file_${index}`, s.customizationFile, s.customizationFile.name);
          }
          return {
            sku: s.sku,
            description: s.description,
            category: s.category || '',
            quantity: s.quantity,
            stock: s.stock,
            customization: s.customization || 'no',
            fileIndex: (s.customization === 'yes' && s.customizationFile) ? index : null
          };
        });
        formData.append('selections', JSON.stringify(selectionsData));
        formData.append('pickupCity', pickupCity || '');
        formData.append('pickupStore', pickupStore || '');

        // --- Call /api/checkout to reserve stock + create Razorpay order ---
        const checkoutRes = await fetch(`${API_BASE}/api/checkout`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        const checkoutData = await checkoutRes.json();

        if (!checkoutRes.ok) {
          throw new Error(checkoutData.error || 'Checkout failed. Please try again.');
        }

        // ₹0 / free cart — server finalised it directly, no modal needed
        if (checkoutData.free) {
          setSubmittedCount(selections.length);
          setShowSuccess(true);
          setSelectedProducts({});
          setCurrentPage('products');
          fetchProducts();
          setSubmitting(false);
          return;
        }

        // --- Load Razorpay Checkout.js ---
        const RazorpayConstructor = await loadRazorpay();
        if (!RazorpayConstructor) {
          throw new Error('Could not load the payment window. Please check your connection and try again.');
        }

        const { razorpayOrderId, keyId, amount, currency, prefill } = checkoutData;

        // --- Open the Razorpay modal ---
        await new Promise((resolve, reject) => {
          const rzp = new RazorpayConstructor({
            key: keyId,
            order_id: razorpayOrderId,
            amount,
            currency,
            name: 'Printo Employee Shopping',
            prefill,
            theme: { color: '#2563eb' },

            handler: async (response) => {
              // Payment captured — verify server-side
              try {
                const verifyRes = await fetch(`${API_BASE}/api/payment/verify`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                  },
                  body: JSON.stringify({
                    razorpay_order_id: response.razorpay_order_id,
                    razorpay_payment_id: response.razorpay_payment_id,
                    razorpay_signature: response.razorpay_signature,
                    razorpayOrderId
                  })
                });
                const verifyData = await verifyRes.json();
                if (!verifyRes.ok) {
                  reject(new Error(verifyData.error || 'Payment verification failed. Please contact support.'));
                  return;
                }
                resolve(verifyData);
              } catch (e) {
                reject(e);
              }
            },

            modal: {
              ondismiss: () => {
                // User closed the modal — best-effort cancel to release the hold
                fetch(`${API_BASE}/api/payment/cancel`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                  },
                  body: JSON.stringify({ razorpayOrderId })
                }).catch(() => {});
                reject(new Error(`Payment cancelled. Your cart items are still saved — you can try again anytime.`));
              }
            }
          });

          rzp.open();
        });

        // Verify resolved — order placed successfully
        setSubmittedCount(selections.length);
        setShowSuccess(true);
        setSelectedProducts({});
        setCurrentPage('products');
        fetchProducts();

      } catch (err) {
        setError(err.message);
      } finally {
        setSubmitting(false);
      }
      return;
    }

    try {
      const formData = new FormData();

      // Per-product files
      const selectionsData = selections.map((s, index) => {
        if (s.customization === 'yes' && s.customizationFile) {
          formData.append(`file_${index}`, s.customizationFile, s.customizationFile.name);
        }
        return {
          sku: s.sku,
          description: s.description,
          category: s.category || '',
          quantity: s.quantity,
          stock: s.stock,
          customization: s.customization || 'no',
          fileIndex: (s.customization === 'yes' && s.customizationFile) ? index : null
        };
      });

      formData.append('selections', JSON.stringify(selectionsData));
      formData.append('pickupCity', pickupCity || '');
      formData.append('pickupStore', pickupStore || '');

      const response = await fetch(`${API_BASE}/api/submit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // No Content-Type header — browser sets it with boundary for FormData
        },
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit selections');
      }

      setSubmittedCount(selections.length);
      setShowSuccess(true);
      setSelectedProducts({});
      setCurrentPage('products');

      // Refresh products to show updated stock
      fetchProducts();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Distinct categories (from the sheet's Category column) for the pill filter.
  const categories = useMemo(() => {
    const set = new Set();
    products.forEach(p => { if (p.category) set.add(p.category); });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [products]);

  // If the selected category disappears (e.g. after a refresh), fall back to "All".
  useEffect(() => {
    if (selectedCategory !== 'All' && !categories.includes(selectedCategory)) {
      setSelectedCategory('All');
    }
  }, [categories, selectedCategory]);

  // Fuzzy search (typo-tolerant, SKU-weighted), then narrow by the selected category.
  const fuse = useMemo(() => createProductFuse(products), [products]);
  const searchResults = useMemo(
    () => searchProducts(fuse, products, searchTerm),
    [fuse, products, searchTerm]
  );
  const filteredProducts = useMemo(
    () => (selectedCategory === 'All'
      ? searchResults
      : searchResults.filter(p => p.category === selectedCategory)),
    [searchResults, selectedCategory]
  );
  const activeFilteredCount = useMemo(
    () => filteredProducts.filter(p => !p.outOfStock).length,
    [filteredProducts]
  );

  const selectedCount = Object.keys(selectedProducts).length;

  // Show login page
  if (!user || !token) {
    if (currentPage === 'register') {
      return (
        <Register
          onRegisterSuccess={() => {
            setGoogleData(null);
            setCurrentPage('login');
          }}
          onBackToLogin={() => {
            setGoogleData(null);
            setCurrentPage('login');
          }}
          onLogin={handleLogin}
          googleData={googleData}
        />
      );
    }
    return (
      <Login
        onLogin={handleLogin}
        onRegister={() => {
          setGoogleData(null);
          setCurrentPage('register');
        }}
        onGoogleRegister={handleGoogleRegister}
      />
    );
  }

  // Show admin dashboard
  if (currentPage === 'admin') {
    return (
      <AdminDashboard
        user={user}
        token={token}
        onBack={() => setCurrentPage('products')}
        onLogout={handleLogout}
      />
    );
  }

  const headerTitle = 'Employee Shopping';
  const headerSubtitle = '';

  const showSearch = true;
  const showCartSummary = currentPage === 'products';
  const showCategoryFilter = currentPage === 'products';
  const showProductsHeader = currentPage === 'products';
  const showProductGrid = currentPage === 'products';
  const handleGoHome = () => { setError(''); setCurrentPage('products'); };

  // Show products page
  return (
    <div className="app-container">
      <Header
        user={user}
        onLogout={handleLogout}
        onHomeClick={handleGoHome}
        onCartClick={() => { setError(''); setCurrentPage('cart'); }}
        onAdminClick={() => setCurrentPage('admin')}
        onOrdersClick={() => { setError(''); setCurrentPage('orders'); }}
        currentPage={currentPage}
        products={products}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        refreshing={loading}
        title={headerTitle}
        subtitle={headerSubtitle}
        showSearch={showSearch}
        onBack={null}
      />

      <main className={`main-content ${currentPage === 'cart' ? 'cart-main-content' : ''}`}>

        {error && currentPage !== 'cart' && (
          <div className="error-message">
            {error}
            <button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>x</button>
          </div>
        )}

        {showCategoryFilter && (
          <CategoryFilter
            categories={categories}
            selected={selectedCategory}
            onSelect={setSelectedCategory}
          />
        )}

        {showProductsHeader && (
          <div className="products-header">
            <span className="products-count">
              {activeFilteredCount} active product{activeFilteredCount !== 1 ? 's' : ''} found ({filteredProducts.length} total)
            </span>
          </div>
        )}

        {showProductGrid && (
          <ProductGrid
            products={filteredProducts}
            selectedProducts={selectedProducts}
            onAddToCart={handleAddToCart}
            onRemove={handleRemoveFromCart}
            onQuantityChange={handleQuantityChange}
            loading={loading}
          />
        )}

        {currentPage === 'orders' && (
          <OrderHistory
            token={token}
          />
        )}

        {currentPage === 'cart' && (
          <CartPage
            token={token}
            selectedProducts={selectedProducts}
            onQuantityChange={handleQuantityChange}
            onRemove={handleRemoveFromCart}
            onPlaceOrder={handlePlaceOrder}
            submitting={submitting}
            error={error}
            onClearError={() => setError('')}
            paymentMethod={paymentConfig.paymentMethod}
          />
        )}
      </main>

      {showCartSummary && (
        <CartSummary
          selectedCount={selectedCount}
          selectedProducts={selectedProducts}
          onClear={handleClearSelection}
          onConfirm={() => { setError(''); setCurrentPage('cart'); }}
          submitting={submitting}
        />
      )}

      {pendingProduct && (
        <CustomizationModal
          product={pendingProduct}
          onConfirm={handleCustomizationConfirm}
          onCancel={handleCustomizationCancel}
        />
      )}

      {showSuccess && (
        <SuccessModal
          onClose={() => setShowSuccess(false)}
          count={submittedCount}
        />
      )}
    </div>
  );
}

export default App;
